import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ArrowLeft, Settings, Plus, Download, ArrowRightLeft, Phone } from 'lucide-react';
import { AlertModal } from './AlertModal';
import { WalletChargeSummary } from './WalletChargeSummary';
import { WalletChargeDaily } from './WalletChargeDaily';
import { WalletChargeTransactions } from './WalletChargeTransactions';
import { UsageSummary } from './UsageSummary';
import { ListScrubberStats } from './ListScrubberStats';
import { UsageDaily } from './UsageDaily';
import { UsageTransactions } from './UsageTransactions';
import { ExportDialog } from './ExportDialog';
import { ChangePlanModal } from './ChangePlanModal';
import { WalletSettings } from './WalletSettings';
import { AddBalanceModal } from './AddBalanceModal';
import { TransferFundsModal } from './TransferFundsModal';
import { PhoneManagementModal } from './PhoneManagementModal';
import { Toast } from './Toast';
import { DowngradeBanner } from './DowngradeBanner';

// View state type for drill-down navigation (usage summary)
type WalletView = 'summary' | 'daily' | 'transactions';

// View state type for wallet charge drill-down
type WalletChargeView = 'summary' | 'daily' | 'transactions';

// Plan type for current_plan object
interface Plan {
  id: string;
  plan_name: string;
  plan_type: string;
  includes_mobile: boolean;
  billing_period: string;
  price: number;
}

interface BillingInfo {
  wallet_balance: number;
  plan_id: string | null;
  plan_name: string | null;
  billing_cycle_start: string | null;
  billing_cycle_end: string | null;
  auto_recharge_enabled: boolean;
  auto_recharge_threshold: number;
  auto_recharge_amount: number;
  has_payment_method: boolean;
  // New fields for ChangePlanModal
  current_plan: Plan | null;
  scheduled_plan_id: string | null;
  scheduled_plan_name: string | null;
  scheduled_plan_change_date: string | null;
  cancellation_scheduled_date: string | null;
  stripe_subscription_id: string | null;
  // Internal user flag
  is_internal?: boolean;
  // Billing suspension
  billing_suspended_at: string | null;
  // Past due / expired flags
  plan_past_due?: boolean;
  plan_expired?: boolean;
  suspension_cause?: 'plan' | 'balance' | 'both' | null;
  // Authoritative subscription payment status (dunning / lapse), independent of dates
  subscription_payment_status?: {
    state: 'current' | 'failing' | 'lapsed';
    failed_amount: number | null;
    invoice_date: string | null;
    next_retry_at: string | null;
    attempt_count: number | null;
    hosted_invoice_url: string | null;
    lapsed_plan_name: string | null;
    suspend_after_attempts: number;
  };
}

interface WalletPageProps {
  locationId: string;
  onBack: () => void;
}

// Data types for new sections
interface WalletChargeCategory {
  category: 'manual_topup' | 'auto_topup' | 'admin_adjustments';
  displayName: string;
  count: number;
  total_amount: number;
}

interface UsageCategory {
  category: string;
  count: number;
  unit: string;
  total_cost: number;
}

interface ScrubberStats {
  contacts_processed: number;
  messages_sent: number;
  landlines_removed: number;
  tcpa_removed: number;
}

// Dropdown options for auto-recharge amount (min $10, max $1,000 for custom)
const RECHARGE_AMOUNTS = [10, 25, 50, 100, 250, 500];
const RECHARGE_AMOUNT_MIN = 10;
const RECHARGE_AMOUNT_MAX = 1000;

// Dropdown options for auto-recharge threshold (min $1, max $500 for custom)
const RECHARGE_THRESHOLDS = [10, 25, 50, 100];
const RECHARGE_THRESHOLD_MIN = 1;
const RECHARGE_THRESHOLD_MAX = 500;

// Helper to get current month date range
const getCurrentMonthRange = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: startOfMonth.toISOString().split('T')[0],
    end: endOfMonth.toISOString().split('T')[0],
  };
};

export function WalletPage({ locationId, onBack }: WalletPageProps) {
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Auto-recharge form state
  const [rechargeAmount, setRechargeAmount] = useState<number | 'other'>(50);
  const [rechargeThreshold, setRechargeThreshold] = useState<number | 'other'>(25);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [customThreshold, setCustomThreshold] = useState<string>('');
  const [savingRecharge, setSavingRecharge] = useState(false);

  // Date range state (default to current month)
  const [dateRange, setDateRange] = useState(getCurrentMonthRange);

  // Wallet charges state (now category-based)
  const [walletChargeCategories, setWalletChargeCategories] = useState<WalletChargeCategory[]>([]);
  const [walletChargesTotal, setWalletChargesTotal] = useState(0);
  const [walletChargesLoading, setWalletChargesLoading] = useState(false);
  const [walletChargesError, setWalletChargesError] = useState<string | null>(null);

  // Wallet charge drill-down navigation state
  const [walletChargeView, setWalletChargeView] = useState<WalletChargeView>('summary');
  const [selectedWalletCategory, setSelectedWalletCategory] = useState<string | null>(null);
  const [selectedWalletDate, setSelectedWalletDate] = useState<string | null>(null);

  // Usage summary state
  const [usageCategories, setUsageCategories] = useState<UsageCategory[]>([]);
  const [usageTotalCost, setUsageTotalCost] = useState(0);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);

  // Scrubber stats state
  const [scrubberStats, setScrubberStats] = useState<ScrubberStats>({
    contacts_processed: 0,
    messages_sent: 0,
    landlines_removed: 0,
    tcpa_removed: 0,
  });
  const [scrubberLoading, setScrubberLoading] = useState(false);
  const [scrubberError, setScrubberError] = useState<string | null>(null);

  // Drill-down navigation state
  const [currentView, setCurrentView] = useState<WalletView>('summary');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Alert modal state
  const [alertModal, setAlertModal] = useState<{ isOpen: boolean; title: string; message: string }>({
    isOpen: false,
    title: '',
    message: '',
  });

  // Export dialog state
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  // Change plan modal state
  const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);

  // Wallet settings modal state
  const [isWalletSettingsOpen, setIsWalletSettingsOpen] = useState(false);

  // Add balance modal state
  const [isAddBalanceModalOpen, setIsAddBalanceModalOpen] = useState(false);

  // Transfer funds modal state
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  // Phone management modal state
  const [isPhoneManagementOpen, setIsPhoneManagementOpen] = useState(false);
  const [phoneLineCount, setPhoneLineCount] = useState<number>(0);

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Cancelling downgrade/cancellation state
  const [cancellingDowngrade, setCancellingDowngrade] = useState(false);
  const [cancellingCancellation, setCancellingCancellation] = useState(false);
  const [retryingPayment, setRetryingPayment] = useState(false);
  const [retryPaymentResult, setRetryPaymentResult] = useState<{ success: boolean; message: string } | null>(null);

  // Track if account was just unsuspended (to show reactivation banner)
  const [wasJustUnsuspended, setWasJustUnsuspended] = useState(false);

  const showAlert = (title: string, message: string) => {
    setAlertModal({ isOpen: true, title, message });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, title: '', message: '' });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const fetchBilling = useCallback(async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}`,
        { credentials: 'include' }
      );

      const data = (await response.json()) as { success: boolean; billing?: BillingInfo; error?: string };

      if (data.success && data.billing) {
        // Detect unsuspension: was suspended before, now cleared
        setBilling(prev => {
          if (prev?.billing_suspended_at && !data.billing!.billing_suspended_at) {
            setWasJustUnsuspended(true);
          }
          return data.billing!;
        });

        // Initialize form state from billing data
        const amount = data.billing.auto_recharge_amount;
        if (RECHARGE_AMOUNTS.includes(amount)) {
          setRechargeAmount(amount);
        } else if (amount > 0) {
          setRechargeAmount('other');
          setCustomAmount(amount.toString());
        } else {
          setRechargeAmount(50); // Default
        }

        const threshold = data.billing.auto_recharge_threshold;
        if (RECHARGE_THRESHOLDS.includes(threshold)) {
          setRechargeThreshold(threshold);
        } else if (threshold > 0) {
          setRechargeThreshold('other');
          setCustomThreshold(threshold.toString());
        } else {
          setRechargeThreshold(25); // Default
        }
      } else {
        setError(data.error || 'Failed to load billing information');
      }
    } catch (err) {
      console.error('[WalletPage] Fetch error:', err);
      setError('Failed to load billing information');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [locationId]);

  // Fetch wallet charges summary for date range
  const fetchWalletCharges = useCallback(async () => {
    setWalletChargesLoading(true);
    setWalletChargesError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/wallet-charges-summary?start=${dateRange.start}&end=${dateRange.end}`,
        { credentials: 'include' }
      );
      const data = (await response.json()) as {
        success: boolean;
        categories?: WalletChargeCategory[];
        total?: number;
        error?: string;
      };
      if (data.success) {
        setWalletChargeCategories(data.categories || []);
        setWalletChargesTotal(data.total || 0);
      } else {
        setWalletChargesError(data.error || 'Failed to load wallet charges');
      }
    } catch (err) {
      console.error('[WalletPage] Wallet charges fetch error:', err);
      setWalletChargesError('Failed to load wallet charges');
    } finally {
      setWalletChargesLoading(false);
    }
  }, [locationId, dateRange]);

  // Fetch usage summary for date range
  const fetchUsageSummary = useCallback(async () => {
    setUsageLoading(true);
    setUsageError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/usage-summary?start=${dateRange.start}&end=${dateRange.end}`,
        { credentials: 'include' }
      );
      const data = (await response.json()) as {
        success: boolean;
        categories?: UsageCategory[];
        total_cost?: number;
        error?: string;
      };
      if (data.success) {
        setUsageCategories(data.categories || []);
        setUsageTotalCost(data.total_cost || 0);
      } else {
        setUsageError(data.error || 'Failed to load usage summary');
      }
    } catch (err) {
      console.error('[WalletPage] Usage summary fetch error:', err);
      setUsageError('Failed to load usage summary');
    } finally {
      setUsageLoading(false);
    }
  }, [locationId, dateRange]);

  // Fetch scrubber stats for date range
  const fetchScrubberStats = useCallback(async () => {
    setScrubberLoading(true);
    setScrubberError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/scrubber-stats?start=${dateRange.start}&end=${dateRange.end}`,
        { credentials: 'include' }
      );
      const data = (await response.json()) as {
        success: boolean;
        stats?: ScrubberStats;
        error?: string;
      };
      if (data.success && data.stats) {
        setScrubberStats(data.stats);
      } else {
        setScrubberError(data.error || 'Failed to load scrubber stats');
      }
    } catch (err) {
      console.error('[WalletPage] Scrubber stats fetch error:', err);
      setScrubberError('Failed to load scrubber stats');
    } finally {
      setScrubberLoading(false);
    }
  }, [locationId, dateRange]);

  // Fetch phone line count (not date-range dependent)
  const fetchPhoneLineCount = useCallback(async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/phones`,
        { credentials: 'include' }
      );
      const data = await response.json() as { success: boolean; count?: number };
      if (data.success) {
        setPhoneLineCount(data.count || 0);
      }
    } catch {
      // Non-critical, just leave count at 0
    }
  }, [locationId]);

  useEffect(() => {
    fetchBilling();
    fetchPhoneLineCount();
  }, [fetchBilling, fetchPhoneLineCount]);

  // Fetch report data when date range changes
  useEffect(() => {
    fetchWalletCharges();
    fetchUsageSummary();
    fetchScrubberStats();
  }, [fetchWalletCharges, fetchUsageSummary, fetchScrubberStats]);

  const handleRefresh = () => {
    setRefreshing(true);
    // Refresh all data: billing info AND reports
    fetchBilling();
    fetchWalletCharges();
    fetchUsageSummary();
    fetchScrubberStats();
  };

  const handleSaveAutoRecharge = async () => {
    setSavingRecharge(true);

    const amount = rechargeAmount === 'other' ? parseFloat(customAmount) : rechargeAmount;
    const threshold = rechargeThreshold === 'other' ? parseFloat(customThreshold) : rechargeThreshold;

    if (isNaN(amount) || amount < RECHARGE_AMOUNT_MIN || amount > RECHARGE_AMOUNT_MAX) {
      showAlert('Invalid Amount', `Recharge amount must be between ${formatCurrency(RECHARGE_AMOUNT_MIN)} and ${formatCurrency(RECHARGE_AMOUNT_MAX)}.`);
      setSavingRecharge(false);
      return;
    }

    if (isNaN(threshold) || threshold < RECHARGE_THRESHOLD_MIN || threshold > RECHARGE_THRESHOLD_MAX) {
      showAlert('Invalid Threshold', `Threshold must be between ${formatCurrency(RECHARGE_THRESHOLD_MIN)} and ${formatCurrency(RECHARGE_THRESHOLD_MAX)}.`);
      setSavingRecharge(false);
      return;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/auto-recharge`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            auto_recharge_amount: amount,
            auto_recharge_threshold: threshold,
          }),
        }
      );

      const data = (await response.json()) as { success: boolean; error?: string };

      if (data.success) {
        // Refresh billing data
        fetchBilling();
      } else {
        showAlert('Error', data.error || 'Failed to save settings');
      }
    } catch (err) {
      console.error('[WalletPage] Save error:', err);
      showAlert('Error', 'Failed to save settings. Please try again.');
    } finally {
      setSavingRecharge(false);
    }
  };

  const handleAddBalance = () => {
    setIsAddBalanceModalOpen(true);
  };

  const handleBalanceAdded = (newBalance: number) => {
    // Update the local billing state with new balance
    if (billing) {
      setBilling({ ...billing, wallet_balance: newBalance });
    }
    // Show success toast
    setToast({
      message: `Balance added successfully! New balance: ${formatCurrency(newBalance)}`,
      type: 'success',
    });
    // Refresh all data: billing info AND reports
    fetchBilling();
    fetchWalletCharges();
    fetchUsageSummary();
  };

  const handleTransfer = () => {
    setIsTransferModalOpen(true);
  };

  const handleTransferComplete = (newBalance: number) => {
    // Update the local billing state with new balance
    if (billing) {
      setBilling({ ...billing, wallet_balance: newBalance });
    }
    // Show success toast
    setToast({
      message: `Transfer complete! New balance: ${formatCurrency(newBalance)}`,
      type: 'success',
    });
    // Refresh all data: billing info AND reports
    fetchBilling();
    fetchWalletCharges();
    fetchUsageSummary();
  };

  const handleChangePlan = () => {
    setIsChangePlanModalOpen(true);
  };

  const handlePlanChanged = () => {
    // Refresh all data after plan change: billing info AND reports
    fetchBilling();
    fetchWalletCharges();
    fetchUsageSummary();
    fetchScrubberStats();
  };

  const handleRetryPayment = async () => {
    setRetryingPayment(true);
    setRetryPaymentResult(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/retry-subscription`,
        { method: 'POST', credentials: 'include' }
      );
      const data = await response.json() as { success: boolean; message?: string; error?: string };
      if (data.success) {
        setRetryPaymentResult({ success: true, message: data.message || 'Payment processed successfully!' });
        // Refresh billing data and reports to reflect the new payment
        fetchBilling();
        fetchWalletCharges();
        fetchUsageSummary();
      } else {
        setRetryPaymentResult({ success: false, message: data.error || 'Payment failed. Please try a different card.' });
      }
    } catch {
      setRetryPaymentResult({ success: false, message: 'Unable to process payment. Please try again.' });
    } finally {
      setRetryingPayment(false);
    }
  };

  const handleCancelDowngrade = async () => {
    setCancellingDowngrade(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/cancel-downgrade`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        setToast({
          message: 'Scheduled downgrade cancelled successfully',
          type: 'success',
        });
        // Refresh billing data to update UI
        fetchBilling();
      } else {
        showAlert('Error', data.error || 'Failed to cancel scheduled downgrade');
      }
    } catch (err) {
      console.error('[WalletPage] Cancel downgrade error:', err);
      showAlert('Error', 'Failed to cancel scheduled downgrade. Please try again.');
    } finally {
      setCancellingDowngrade(false);
    }
  };

  const handleCancelScheduledCancellation = async () => {
    setCancellingCancellation(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/cancel-scheduled-cancellation`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        setToast({
          message: 'Your plan will continue as normal',
          type: 'success',
        });
        // Refresh billing data to update UI
        fetchBilling();
      } else {
        showAlert('Error', data.error || 'Failed to keep plan');
      }
    } catch (err) {
      console.error('[WalletPage] Cancel scheduled cancellation error:', err);
      showAlert('Error', 'Failed to keep plan. Please try again.');
    } finally {
      setCancellingCancellation(false);
    }
  };

  const handleWalletSettings = () => {
    setIsWalletSettingsOpen(true);
  };

  const handlePaymentMethodsChanged = () => {
    // Refresh billing data when payment methods change
    fetchBilling();
  };

  const handleDownload = () => {
    setIsExportDialogOpen(true);
  };

  // Usage summary navigation handlers
  const handleViewUsageDetails = (category: string) => {
    setSelectedCategory(category);
    setCurrentView('daily');
  };

  const handleBackToSummary = () => {
    setCurrentView('summary');
    setSelectedCategory(null);
    setSelectedDate(null);
  };

  const handleBackToDaily = () => {
    setCurrentView('daily');
    setSelectedDate(null);
  };

  const handleViewTransactions = (date: string) => {
    setSelectedDate(date);
    setCurrentView('transactions');
  };

  // Wallet charge navigation handlers
  const handleViewWalletChargeDetails = (category: string) => {
    setSelectedWalletCategory(category);
    setWalletChargeView('daily');
  };

  const handleBackToWalletSummary = () => {
    setWalletChargeView('summary');
    setSelectedWalletCategory(null);
    setSelectedWalletDate(null);
  };

  const handleBackToWalletDaily = () => {
    setWalletChargeView('daily');
    setSelectedWalletDate(null);
  };

  const handleViewWalletTransactions = (date: string) => {
    setSelectedWalletDate(date);
    setWalletChargeView('transactions');
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
  };

  // Clamp custom amount on blur
  const handleCustomAmountBlur = () => {
    const value = parseFloat(customAmount);
    if (isNaN(value) || value < RECHARGE_AMOUNT_MIN) {
      setCustomAmount(RECHARGE_AMOUNT_MIN.toString());
    } else if (value > RECHARGE_AMOUNT_MAX) {
      setCustomAmount(RECHARGE_AMOUNT_MAX.toString());
    }
  };

  // Clamp custom threshold on blur
  const handleCustomThresholdBlur = () => {
    const value = parseFloat(customThreshold);
    if (isNaN(value) || value < RECHARGE_THRESHOLD_MIN) {
      setCustomThreshold(RECHARGE_THRESHOLD_MIN.toString());
    } else if (value > RECHARGE_THRESHOLD_MAX) {
      setCustomThreshold(RECHARGE_THRESHOLD_MAX.toString());
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button
              onClick={onBack}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Billing & Wallet Details</h1>
        </div>

        {/* Account reactivated banner */}
        {wasJustUnsuspended && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-green-800">Account Reactivated</p>
              <p className="text-sm text-green-700 mt-1">
                Your account has been reactivated. Any batches that were paused due to billing can be resumed from the batch list.
              </p>
            </div>
            <button
              onClick={() => setWasJustUnsuspended(false)}
              className="text-green-400 hover:text-green-600 flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Account suspended warning */}
        {billing?.billing_suspended_at && !billing?.is_internal && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-red-800">Account Suspended</p>
              <p className="text-sm text-red-700 mt-1">
                {billing?.suspension_cause === 'plan'
                  ? 'Your account has been suspended because your plan payment could not be processed. Please resolve your plan payment to reactivate your account.'
                  : billing?.suspension_cause === 'both'
                  ? 'Your account has been suspended due to an unpaid plan payment and a negative wallet balance. Resolve your plan payment and add funds to reactivate your account.'
                  : 'Your account has been suspended due to a negative balance. Please add funds to reactivate your account.'}
              </p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow border border-gray-200 p-6 md:p-8">
          {/* Row 1: Wallet Balance */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-6 border-b border-gray-200 mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <p className="text-sm font-semibold text-gray-600">Wallet Balance</p>
                {billing?.is_internal && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Internal User
                  </span>
                )}
              </div>
              <p className="text-4xl font-bold text-gray-900">
                {formatCurrency(billing?.wallet_balance ?? 0)}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleAddBalance}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Balance
              </button>
              <button
                onClick={handleTransfer}
                className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-colors"
              >
                <ArrowRightLeft className="w-5 h-5" />
                Transfer
              </button>
            </div>
          </div>

          {/* Row 2: Auto Recharge */}
          <div className="flex flex-wrap items-center gap-4 pb-6 border-b border-gray-200 mb-6">
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                Auto-recharge
              </label>
              <select
                value={rechargeAmount}
                onChange={(e) => setRechargeAmount(e.target.value === 'other' ? 'other' : parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded text-sm min-w-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {RECHARGE_AMOUNTS.map((amt) => (
                  <option key={amt} value={amt}>
                    {formatCurrency(amt)}
                  </option>
                ))}
                <option value="other">Other...</option>
              </select>
              {rechargeAmount === 'other' && (
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  onBlur={handleCustomAmountBlur}
                  placeholder="Amount"
                  min={RECHARGE_AMOUNT_MIN}
                  max={RECHARGE_AMOUNT_MAX}
                  className="w-24 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                when balance falls below
              </label>
              <select
                value={rechargeThreshold}
                onChange={(e) => setRechargeThreshold(e.target.value === 'other' ? 'other' : parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded text-sm min-w-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {RECHARGE_THRESHOLDS.map((amt) => (
                  <option key={amt} value={amt}>
                    {formatCurrency(amt)}
                  </option>
                ))}
                <option value="other">Other...</option>
              </select>
              {rechargeThreshold === 'other' && (
                <input
                  type="number"
                  value={customThreshold}
                  onChange={(e) => setCustomThreshold(e.target.value)}
                  onBlur={handleCustomThresholdBlur}
                  placeholder="Amount"
                  min={RECHARGE_THRESHOLD_MIN}
                  max={RECHARGE_THRESHOLD_MAX}
                  className="w-24 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>

            <div className="flex items-center gap-3 ml-auto">
              <button
                onClick={handleSaveAutoRecharge}
                disabled={savingRecharge}
                className="px-4 py-2 bg-blue-600 text-white font-semibold text-sm rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {savingRecharge ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleWalletSettings}
                className="p-2 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                title="Wallet Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Row 3: Plan Info */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div className="flex flex-wrap gap-8">
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">Current Plan</p>
                {billing?.is_internal ? (
                  <p className="text-base font-bold text-purple-700">Internal User</p>
                ) : (
                  <p className="text-base font-bold text-gray-900">
                    {billing?.plan_name || 'No plan selected'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">Next Invoice</p>
                {billing?.is_internal ? (
                  <p className="text-base font-bold text-gray-500">N/A</p>
                ) : billing?.subscription_payment_status?.state === 'lapsed' ? (
                  <p className="text-base font-bold text-red-600">Cancelled</p>
                ) : billing?.subscription_payment_status?.state === 'failing' || billing?.plan_past_due || billing?.plan_expired ? (
                  <p className="text-base font-bold text-red-600">Payment Failed</p>
                ) : (
                  <p className="text-base font-bold text-gray-900">
                    {formatDate(billing?.billing_cycle_end)}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleChangePlan}
              className="px-5 py-2.5 bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-colors"
            >
              Change Plan
            </button>
          </div>

          {/* Plan Payment Problem Banner (dunning window — payment failing, retries pending) */}
          {billing?.subscription_payment_status?.state !== 'lapsed' &&
            (billing?.subscription_payment_status?.state === 'failing' || billing?.plan_past_due) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800">Plan Payment Problem</p>
                  <p className="text-sm text-red-700 mt-1">
                    {billing?.subscription_payment_status?.failed_amount != null
                      ? `Your plan payment of ${formatCurrency(billing.subscription_payment_status.failed_amount)} didn't go through.`
                      : `Your most recent plan payment didn't go through.`}
                    {billing?.subscription_payment_status?.next_retry_at
                      ? ` We'll automatically retry on ${formatDate(billing.subscription_payment_status.next_retry_at)} — or pay now to keep your service uninterrupted.`
                      : ` Please pay now or update your card to keep your service uninterrupted.`}
                  </p>
                </div>
                <div className="flex-shrink-0 flex gap-2">
                  <button
                    onClick={handleRetryPayment}
                    disabled={retryingPayment}
                    className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {retryingPayment ? 'Retrying...' : 'Retry Payment'}
                  </button>
                  <button
                    onClick={handleWalletSettings}
                    className="px-4 py-2 bg-white text-red-700 text-sm font-semibold rounded border border-red-300 hover:bg-red-50 transition-colors"
                  >
                    Update Card
                  </button>
                </div>
              </div>
              {retryPaymentResult && (
                <div className={`mt-3 text-sm p-2 rounded ${retryPaymentResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-100 text-red-800 border border-red-300'}`}>
                  {retryPaymentResult.message}
                </div>
              )}
            </div>
          )}

          {/* Plan Lapsed Banner (cancelled because payment could not be collected) */}
          {billing?.subscription_payment_status?.state === 'lapsed' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800">Plan Cancelled — Payment Could Not Be Collected</p>
                  <p className="text-sm text-red-700 mt-1">
                    {billing?.subscription_payment_status?.lapsed_plan_name
                      ? `Your ${billing.subscription_payment_status.lapsed_plan_name} plan was cancelled because we couldn't process payment after several attempts. Re-subscribe to restore your service.`
                      : `Your plan was cancelled because we couldn't process payment after several attempts. Re-subscribe to restore your service.`}
                  </p>
                  {billing?.subscription_payment_status?.hosted_invoice_url && (
                    <a
                      href={billing.subscription_payment_status.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block text-sm font-semibold text-red-700 underline mt-2 hover:text-red-800"
                    >
                      Pay your outstanding invoice
                    </a>
                  )}
                </div>
                <div className="flex-shrink-0 flex gap-2">
                  <button
                    onClick={handleChangePlan}
                    className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded hover:bg-red-700 transition-colors"
                  >
                    Choose a Plan
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Scheduled Downgrade Banner (only shown when downgrade is scheduled) */}
          {billing?.scheduled_plan_id && billing.scheduled_plan_name && billing.scheduled_plan_change_date && (
            <DowngradeBanner
              variant="wallet"
              type="downgrade"
              scheduledPlanName={billing.scheduled_plan_name}
              scheduledDate={billing.scheduled_plan_change_date}
              onCancel={handleCancelDowngrade}
              cancelling={cancellingDowngrade}
            />
          )}

          {/* Scheduled Cancellation Banner (only shown when cancellation is scheduled) */}
          {billing?.cancellation_scheduled_date && !billing.scheduled_plan_id && (
            <DowngradeBanner
              variant="wallet"
              type="cancellation"
              scheduledDate={billing.cancellation_scheduled_date}
              onCancel={handleCancelScheduledCancellation}
              cancelling={cancellingCancellation}
            />
          )}

          {/* Row 4: Phone Lines */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pt-6 border-t border-gray-200 mt-6">
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Phone Lines</p>
              <p className="text-base font-bold text-gray-900">
                {phoneLineCount} active line{phoneLineCount !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={() => setIsPhoneManagementOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700 transition-colors"
            >
              <Phone className="w-5 h-5" />
              Manage Phone Lines
            </button>
          </div>
        </div>

        {/* Date Range Picker & Export Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-semibold text-gray-700">Date Range:</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
        </div>

        {/* Conditional Content Based on View */}
        {currentView === 'summary' && walletChargeView === 'summary' ? (
          <>
            {/* Wallet Charge Summary (full width, first) */}
            <div className="mb-6">
              <WalletChargeSummary
                categories={walletChargeCategories}
                total={walletChargesTotal}
                loading={walletChargesLoading}
                error={walletChargesError}
                onViewDetails={handleViewWalletChargeDetails}
              />
            </div>

            {/* Usage Summary (full width) */}
            <div className="mb-6">
              <UsageSummary
                categories={usageCategories}
                totalCost={usageTotalCost}
                loading={usageLoading}
                error={usageError}
                onViewDetails={handleViewUsageDetails}
              />
            </div>

            {/* List Scrubber Stats (full width, at bottom) */}
            <ListScrubberStats
              stats={scrubberStats}
              loading={scrubberLoading}
              error={scrubberError}
            />
          </>
        ) : walletChargeView === 'daily' && selectedWalletCategory ? (
          <WalletChargeDaily
            locationId={locationId}
            category={selectedWalletCategory}
            dateRange={dateRange}
            onBack={handleBackToWalletSummary}
            onViewTransactions={handleViewWalletTransactions}
          />
        ) : walletChargeView === 'transactions' && selectedWalletCategory && selectedWalletDate ? (
          <WalletChargeTransactions
            locationId={locationId}
            category={selectedWalletCategory}
            date={selectedWalletDate}
            onBack={handleBackToWalletDaily}
            onBackToSummary={handleBackToWalletSummary}
          />
        ) : currentView === 'daily' && selectedCategory ? (
          <UsageDaily
            locationId={locationId}
            category={selectedCategory}
            dateRange={dateRange}
            onBack={handleBackToSummary}
            onViewTransactions={handleViewTransactions}
          />
        ) : currentView === 'transactions' && selectedCategory && selectedDate ? (
          <UsageTransactions
            locationId={locationId}
            category={selectedCategory}
            date={selectedDate}
            onBack={handleBackToDaily}
            onBackToSummary={handleBackToSummary}
          />
        ) : null}
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        onClose={closeAlert}
      />

      {/* Export Dialog */}
      <ExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        locationId={locationId}
        dateRange={dateRange}
      />

      {/* Change Plan Modal */}
      {billing && (
        <ChangePlanModal
          isOpen={isChangePlanModalOpen}
          onClose={() => setIsChangePlanModalOpen(false)}
          locationId={locationId}
          billingInfo={{
            current_plan: billing.current_plan,
            billing_cycle_end: billing.billing_cycle_end,
            has_payment_method: billing.has_payment_method,
            scheduled_plan_id: billing.scheduled_plan_id,
            scheduled_plan_name: billing.scheduled_plan_name,
            scheduled_plan_change_date: billing.scheduled_plan_change_date,
            cancellation_scheduled_date: billing.cancellation_scheduled_date,
          }}
          onPlanChanged={handlePlanChanged}
          isInternal={billing.is_internal}
        />
      )}

      {/* Wallet Settings Modal */}
      <WalletSettings
        isOpen={isWalletSettingsOpen}
        onClose={() => setIsWalletSettingsOpen(false)}
        locationId={locationId}
        onPaymentMethodsChanged={handlePaymentMethodsChanged}
      />

      {/* Add Balance Modal */}
      <AddBalanceModal
        isOpen={isAddBalanceModalOpen}
        onClose={() => setIsAddBalanceModalOpen(false)}
        locationId={locationId}
        currentBalance={billing?.wallet_balance ?? 0}
        onBalanceAdded={handleBalanceAdded}
        isInternal={billing?.is_internal}
      />

      {/* Transfer Funds Modal */}
      <TransferFundsModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        locationId={locationId}
        currentBalance={billing?.wallet_balance ?? 0}
        autoRechargeThreshold={billing?.auto_recharge_threshold ?? 0}
        onTransferComplete={handleTransferComplete}
      />

      {/* Phone Management Modal */}
      <PhoneManagementModal
        isOpen={isPhoneManagementOpen}
        onClose={() => setIsPhoneManagementOpen(false)}
        locationId={locationId}
        currentBalance={billing?.wallet_balance ?? 0}
        isInternal={billing?.is_internal}
        onPhonePurchased={(newBalance) => {
          if (billing) setBilling({ ...billing, wallet_balance: newBalance });
          setToast({ message: 'Phone number purchased!', type: 'success' });
          fetchPhoneLineCount();
        }}
        onPhoneReleased={() => {
          setToast({ message: 'Phone number released', type: 'info' });
          fetchPhoneLineCount();
          fetchBilling();
        }}
      />

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={5000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
